import { IconSizes } from "atoms/constants/icon-sizes";
import NotebookRecord from "models/view-models/notebook-record";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import useNotebookActionPermission, {
    NotebookAction,
} from "pages/notebooks/use-notebook-action-permissions";
import React from "react";
import NotebookPanel from "./notebook-panel";
import SharedPagePanel from "./shared-page-panel";
import PagePanel from "./page-panel";
import SidebarAddPageButton from "../pages/sidebar-add-page-button";
import { t } from "utilities/localization-utils";

const BASE_CLASS_NAME = "c-notebook-sidebar";

interface NotebookItemProps {
    notebook: NotebookRecord;
    notebookId: number;
    isOpen: boolean;
    hasNoPages: boolean;
    shouldExpand: boolean;
    isSharedSection: boolean;
    isSharedPagesNotebook: (notebook: NotebookRecord) => boolean;
    getNotebookClassModifier: (notebookId: number) => string;
    onNotebookTitleClick: (
        id: number,
        isOpen: boolean,
        shouldExpand: boolean
    ) => void;
    onNotebookExpand: (id: number, isOpen: boolean) => void;
    onNotebookClose: (id: number) => void;
}

const NotebookItem: React.FC<NotebookItemProps> = ({
    notebook,
    notebookId,
    isOpen,
    hasNoPages,
    shouldExpand,
    isSharedSection,
    isSharedPagesNotebook,
    getNotebookClassModifier,
    onNotebookTitleClick,
    onNotebookExpand,
    onNotebookClose,
}) => {
    const { can } = useNotebookActionPermission(notebook);
    const canCreatePage = can(NotebookAction.CreatePage);
    const canDelete = can(NotebookAction.Delete);
    const canShare = can(NotebookAction.Share);

    return (
        <div
            className={`${BASE_CLASS_NAME}__item ${getNotebookClassModifier(notebookId)} ${isOpen ? "-open" : ""}`}
            key={notebook.id}>
            <CollapsePanel
                buttonAriaText={`toggle ${notebook.name}`}
                collapse={!isOpen}
                iconSize={IconSizes.Base}
                panelTop={
                    <NotebookPanel
                        notebook={notebook}
                        canDelete={canDelete}
                        canShare={canShare}
                    />
                }
                onTitleClick={() =>
                    onNotebookTitleClick(notebookId, isOpen, shouldExpand)
                }
                onOpen={() => onNotebookExpand(notebookId, isOpen)}
                onClose={() => onNotebookClose(notebookId)}>
                {notebook.pages?.map((page) =>
                    isSharedSection ? (
                        isSharedPagesNotebook(notebook) ? (
                            <SharedPagePanel
                                key={`shared-${page.id}-${page.name}`}
                                page={page}
                                notebook={notebook}
                            />
                        ) : (
                            <PagePanel
                                key={`${page.id}-${page.name}`}
                                page={page}
                                notebookId={notebook.id!}
                            />
                        )
                    ) : (
                        <PagePanel
                            key={`${page.id}-${page.name}`}
                            page={page}
                            notebookId={notebookId}
                        />
                    )
                )}
                {isSharedSection &&
                    isSharedPagesNotebook(notebook) &&
                    hasNoPages && (
                        <div className={`${BASE_CLASS_NAME}__sharedPage`}>
                            <span>
                                {t("notebooks-shared-pages-placeholder")}
                            </span>
                        </div>
                    )}
                {(!isSharedSection || canCreatePage) && (
                    <div className={`${BASE_CLASS_NAME}__addPageButton`}>
                        <SidebarAddPageButton notebookId={notebookId} />
                    </div>
                )}
            </CollapsePanel>
        </div>
    );
};

export default NotebookItem;
